import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Email provider configurations with auto-fill values
const EMAIL_PROVIDERS = {
  gmail: {
    name: 'Gmail',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    use_tls: true,
    use_ssl: false,
    description: 'Use Gmail App Password for authentication',
    helpUrl: 'https://support.google.com/accounts/answer/185833'
  },
  outlook: {
    name: 'Outlook',
    smtp_host: 'smtp-mail.outlook.com',
    smtp_port: 587,
    use_tls: true,
    use_ssl: false,
    description: 'Use your Outlook/Hotmail account',
    helpUrl: 'https://support.microsoft.com/en-us/office/pop-imap-and-smtp-settings-8361e398-8af4-4e97-b147-6c6c4ac95353'
  },
  office365: {
    name: 'Office 365',
    smtp_host: 'smtp.office365.com',
    smtp_port: 587,
    use_tls: true,
    use_ssl: false,
    description: 'Use your Office 365 business account',
    helpUrl: 'https://docs.microsoft.com/en-us/exchange/mail-flow-best-practices/how-to-set-up-a-multifunction-device-or-application-to-send-email-using-microsoft-365-or-office-365'
  },
  custom: {
    name: 'Custom SMTP',
    smtp_host: '',
    smtp_port: 587,
    use_tls: true,
    use_ssl: false,
    description: 'Configure your own SMTP server',
    helpUrl: null
  }
};

const SmtpSettings = () => {
  // Form state
  const [formData, setFormData] = useState({
    provider: 'gmail',
    smtp_host: EMAIL_PROVIDERS.gmail.smtp_host,
    smtp_port: EMAIL_PROVIDERS.gmail.smtp_port,
    username: '',
    password: '',
    use_tls: true,
    use_ssl: false,
    from_name: '',
    from_email: ''
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  
  // Status state
  const [currentConfig, setCurrentConfig] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Load existing configuration
  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp-config`);
      if (response.data) {
        const config = response.data;
        setCurrentConfig(config);
        setFormData({
          provider: config.provider,
          smtp_host: config.smtp_host,
          smtp_port: config.smtp_port,
          username: config.username,
          password: '', // Don't load password for security
          use_tls: config.use_tls,
          use_ssl: config.use_ssl,
          from_name: config.from_name || '',
          from_email: config.from_email
        });
      }
    } catch (error) {
      console.error('Error loading SMTP config:', error);
      // Not showing error - just means no config exists yet
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
  };

  const handleProviderChange = (provider) => {
    const providerConfig = EMAIL_PROVIDERS[provider];
    setFormData({
      ...formData,
      provider,
      smtp_host: providerConfig.smtp_host,
      smtp_port: providerConfig.smtp_port,
      use_tls: providerConfig.use_tls,
      use_ssl: providerConfig.use_ssl
    });
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleTlsSslToggle = (type) => {
    if (type === 'tls') {
      setFormData({ ...formData, use_tls: true, use_ssl: false });
    } else {
      setFormData({ ...formData, use_tls: false, use_ssl: true });
    }
  };

  const validateForm = () => {
    const required = ['smtp_host', 'smtp_port', 'username', 'password', 'from_email'];
    for (const field of required) {
      if (!formData[field]) {
        showToast(`Please fill in the ${field.replace('_', ' ')} field`, 'error');
        return false;
      }
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.from_email)) {
      showToast('Please enter a valid From email address', 'error');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp-config`,
        formData
      );
      setCurrentConfig(response.data);
      showToast('SMTP configuration saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving SMTP config:', error);
      showToast(error.response?.data?.detail || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      showToast('Please enter a test email address', 'error');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      showToast('Please enter a valid test email address', 'error');
      return;
    }

    setTesting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp-config/test`,
        { test_email: testEmail }
      );
      
      if (response.data.success) {
        showToast(response.data.message, 'success');
        setShowTestModal(false);
        loadConfiguration(); // Reload to get verified status
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error testing SMTP config:', error);
      showToast(error.response?.data?.detail || 'Failed to send test email', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className={`${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 text-sm`}>
            {toast.type === 'success' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-900">Email / SMTP Configuration</h2>
        <p className="text-slate-500 text-xs mt-0.5">Configure email server settings for sending reports.</p>
      </div>

      {/* Status Card */}
      {currentConfig && (
        <div className={`mb-4 p-3 rounded-lg border ${currentConfig.is_verified ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentConfig.is_verified ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                {currentConfig.is_verified ? (
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
              </div>
              <div>
                <p className={`font-medium text-sm ${currentConfig.is_verified ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {currentConfig.is_verified ? 'Configuration Verified' : 'Not Verified'}
                </p>
                <p className={`text-xs ${currentConfig.is_verified ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {currentConfig.is_verified 
                    ? `Last tested: ${new Date(currentConfig.last_tested_at).toLocaleString()}`
                    : 'Send a test email to verify'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowTestModal(true)}
              className="px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Test Email
            </button>
          </div>
        </div>
      )}

      {/* Provider Selection */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Email Provider</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(EMAIL_PROVIDERS).map(([key, provider]) => (
            <button
              key={key}
              onClick={() => handleProviderChange(key)}
              className={`p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                formData.provider === key
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-center space-x-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  formData.provider === key ? 'bg-emerald-100' : 'bg-slate-100'
                }`}>
                  <svg className={`w-4 h-4 ${formData.provider === key ? 'text-emerald-600' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className={`font-medium text-xs ${formData.provider === key ? 'text-emerald-900' : 'text-slate-900'}`}>
                  {provider.name}
                </p>
              </div>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">
          {EMAIL_PROVIDERS[formData.provider].description}
          {EMAIL_PROVIDERS[formData.provider].helpUrl && (
            <a href={EMAIL_PROVIDERS[formData.provider].helpUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 ml-1">
              Learn more →
            </a>
          )}
        </p>
      </div>

      {/* SMTP Settings */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">SMTP Server Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">SMTP Host <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={formData.smtp_host}
              onChange={(e) => handleInputChange('smtp_host', e.target.value)}
              placeholder="smtp.example.com"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
              disabled={formData.provider !== 'custom'}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">SMTP Port <span className="text-red-500">*</span></label>
            <input
              type="number"
              value={formData.smtp_port}
              onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value))}
              placeholder="587"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
              disabled={formData.provider !== 'custom'}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Email / Username <span className="text-red-500">*</span></label>
            <input
              type="email"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              placeholder="your-email@example.com"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">App Password <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder={currentConfig ? '••••••••••••••••' : 'Enter app password'}
                className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showPassword ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /> : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* TLS/SSL Toggle */}
        <div className="mt-4">
          <label className="block text-xs font-medium text-slate-700 mb-2">Security</label>
          <div className="flex space-x-3">
            <button onClick={() => handleTlsSslToggle('tls')} disabled={formData.provider !== 'custom'}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${formData.use_tls ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'} ${formData.provider !== 'custom' ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <div className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.use_tls ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                  {formData.use_tls && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
                <div className="text-left"><p className="font-medium text-xs text-slate-900">TLS (STARTTLS)</p><p className="text-[10px] text-slate-500">Port 587</p></div>
              </div>
            </button>
            <button onClick={() => handleTlsSslToggle('ssl')} disabled={formData.provider !== 'custom'}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${formData.use_ssl ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'} ${formData.provider !== 'custom' ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <div className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.use_ssl ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                  {formData.use_ssl && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
                <div className="text-left"><p className="font-medium text-xs text-slate-900">SSL</p><p className="text-[10px] text-slate-500">Port 465</p></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Sender Information */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Sender Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">From Name <span className="text-slate-400">(Optional)</span></label>
            <input type="text" value={formData.from_name} onChange={(e) => handleInputChange('from_name', e.target.value)} placeholder="Asset Manager"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">From Email <span className="text-red-500">*</span></label>
            <input type="email" value={formData.from_email} onChange={(e) => handleInputChange('from_email', e.target.value)} placeholder="noreply@example.com"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors" />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button onClick={() => setShowTestModal(true)} disabled={!currentConfig}
          className="px-4 py-2 text-xs border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          <span>Send Test Email</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-700 transition-colors disabled:opacity-50 flex items-center space-x-1.5">
          {saving ? (<><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Saving...</span></>) : (<><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span>Save Configuration</span></>)}
        </button>
      </div>

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-cyan-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <div><h3 className="text-sm font-bold text-slate-900">Send Test Email</h3><p className="text-xs text-slate-500">Verify SMTP configuration</p></div>
                </div>
                <button onClick={() => setShowTestModal(false)} className="p-1.5 hover:bg-white/50 rounded-lg"><svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
            </div>
            <div className="p-4">
              <label className="block text-xs font-medium text-slate-700 mb-1">Test Email Address</label>
              <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your-email@example.com"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors" />
            </div>
            <div className="p-3 border-t border-slate-200 bg-slate-50 flex space-x-2">
              <button onClick={() => setShowTestModal(false)} disabled={testing} className="flex-1 py-2 px-3 text-xs border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-100 disabled:opacity-50">Cancel</button>
              <button onClick={handleTest} disabled={testing || !testEmail}
                className="flex-1 py-2 px-3 text-xs bg-gradient-to-r from-emerald-500 to-cyan-600 text-white rounded-lg font-medium hover:from-emerald-600 hover:to-cyan-700 disabled:opacity-50 flex items-center justify-center space-x-1.5">
                {testing ? (<><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Sending...</span></>) : (<span>Send Test</span>)}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SmtpSettings;
